import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowRight, Eye, EyeOff, Loader2, MailCheck, ShieldCheck } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import LogoLockup from '../../components/shared/LogoLockup';

const RegisterPage = ({ role = 'subscriber' }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { register, isLoading } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    primaryPhone: '',
    password: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [submittedMessage, setSubmittedMessage] = useState('');
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [localLoading, setLocalLoading] = useState(false);

  useEffect(() => {
    const shouldShowVerificationNotice = searchParams.get('notice') === 'verify-email';
    if (!shouldShowVerificationNotice || submittedMessage) return;
    setSubmittedMessage('A verification email has been sent to you. Please check your inbox to continue.');
    setSubmittedEmail(searchParams.get('email') || '');
  }, [searchParams, submittedMessage]);

  const passwordValid = useMemo(() => {
    const password = form.password;
    return (
      password.length >= 12 &&
      /[A-Z]/.test(password) &&
      /[a-z]/.test(password) &&
      /\d/.test(password) &&
      /[!@#$%^&*(),.?":{}|<>]/.test(password)
    );
  }, [form.password]);

  const updateField = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSubmittedMessage('');
    setSubmittedEmail('');

    if (!passwordValid) {
      setError('Password must be 12+ characters and include uppercase, lowercase, number, and special character.');
      return;
    }

    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLocalLoading(true);
    const result = await register({
      firstName: form.firstName,
      lastName: form.lastName,
      email: form.email,
      primaryPhone: form.primaryPhone,
      password: form.password,
      role
    });
    setLocalLoading(false);

    if (!result.success) {
      const detail = result.error;
      setError(typeof detail === 'string' ? detail : detail?.message || 'Registration failed');
      return;
    }

    const nextStep = result.data?.nextStep || result.data?.next_step;
    const needsEmailVerification = (
      result.data?.requiresEmailVerification ||
      result.data?.requires_email_verification ||
      nextStep === 'verify_email' ||
      (!result.data?.access_token && /verify|email/i.test(result.data?.message || ''))
    );

    if (needsEmailVerification) {
      const email = result.data?.email || form.email;
      const message = result.data?.message || (
        role === 'supervisor'
          ? 'A verification email has been sent to your supervisor account email. Please check your inbox before continuing to KYC.'
          : 'A verification email has been sent to you. Please check your inbox to continue.'
      );
      setSubmittedMessage(message);
      setSubmittedEmail(email);
      setSearchParams({ notice: 'verify-email', email }, { replace: true });
      setForm(prev => ({ ...prev, password: '', confirmPassword: '' }));
      return;
    }

    navigate('/kyc', { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#F7F4EE] px-4 py-8">
      <div className="mx-auto w-full max-w-5xl">
        <div className="mb-8 flex items-center justify-between">
          <Link to="/">
            <LogoLockup size="md" />
          </Link>
          <Link to="/login" className="text-sm font-medium text-[#9F1D35] hover:text-[#7A1628]">
            Sign in
          </Link>
        </div>

        <div className="grid gap-8 lg:grid-cols-[0.85fr_1.15fr] lg:items-start">
          <section className="rounded-lg border border-[#E8E6E3] bg-[#FAFAF7] p-7">
            <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-lg bg-[#F5E8EA] text-[#9F1D35]">
              <ShieldCheck className="h-6 w-6" />
            </div>
            <h1 className="font-serif text-4xl font-medium tracking-normal text-[#1A1A1A]">
              {role === 'supervisor' ? 'Create your supervisor account.' : 'Create your Magnolia account.'}
            </h1>
            <p className="mt-4 text-sm leading-7 text-[#5A5A5A]">
              {role === 'supervisor'
                ? 'Start with your account details. Magnolia will take you straight into supervisor KYC before dashboard access opens.'
                : 'Start with your account details. After this, Magnolia will take you straight into subscriber KYC before dashboard access opens.'}
            </p>
            <div className="mt-6 rounded-lg border border-[#C9A961]/40 bg-[#FFF9E8] p-4 text-sm leading-6 text-[#6F5518]">
              {role === 'supervisor'
                ? 'Supervisor KYC is auto-approved for this MVP after submission.'
                : 'Dependents are added after your subscriber KYC is approved.'}
            </div>
          </section>

          {submittedMessage ? (
            <section className="space-y-6 rounded-lg border border-[#E8E6E3] bg-white p-6">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#F5E8EA] text-[#9F1D35]">
                <MailCheck className="h-6 w-6" />
              </div>
              <div>
                <h2 className="font-serif text-3xl font-medium text-[#1A1A1A]">Check your email</h2>
                <p className="mt-3 text-sm leading-7 text-[#5A5A5A]">
                  {submittedMessage}
                </p>
                {submittedEmail && (
                  <p className="mt-3 rounded-lg border border-[#E8E6E3] bg-[#FAFAF7] px-4 py-3 text-sm font-medium text-[#1A1A1A]">
                    {submittedEmail}
                  </p>
                )}
                <p className="mt-4 text-sm leading-6 text-[#5A5A5A]">
                  Open the verification link in that email. Magnolia will sign you in automatically and continue setup from there.
                  If it is not in your inbox, check spam or promotions.
                </p>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row">
                <Button asChild className="flex-1">
                  <Link to="/login">Go to sign in</Link>
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setSubmittedMessage('');
                    setSubmittedEmail('');
                    setSearchParams({}, { replace: true });
                  }}
                >
                  Use another email
                </Button>
              </div>
            </section>
          ) : (
          <form onSubmit={handleSubmit} className="space-y-6 rounded-lg border border-[#E8E6E3] bg-white p-6">
            {error && (
              <div className="rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            )}

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="firstName">First Name</Label>
                <Input id="firstName" value={form.firstName} onChange={(event) => updateField('firstName', event.target.value)} required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">Last Name</Label>
                <Input id="lastName" value={form.lastName} onChange={(event) => updateField('lastName', event.target.value)} required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" value={form.email} onChange={(event) => updateField('email', event.target.value)} required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="primaryPhone">Phone with Country Code</Label>
                <Input id="primaryPhone" type="tel" value={form.primaryPhone} onChange={(event) => updateField('primaryPhone', event.target.value)} placeholder="+1 555 123 4567" required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={form.password}
                    onChange={(event) => updateField('password', event.target.value)}
                    className="pr-12"
                    required
                  />
                  <button
                    type="button"
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-[#5A5A5A]"
                    onClick={() => setShowPassword(value => !value)}
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                <p className={`text-xs ${passwordValid ? 'text-green-700' : 'text-[#5A5A5A]'}`}>
                  12+ characters, uppercase, lowercase, number, and special character.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={form.confirmPassword}
                  onChange={(event) => updateField('confirmPassword', event.target.value)}
                  required
                />
              </div>
            </div>

            <Button type="submit" disabled={localLoading || isLoading} className="w-full">
              {(localLoading || isLoading) ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Creating account...
                </>
              ) : (
                <>
                  {role === 'supervisor' ? 'Create Supervisor Account' : 'Create Account'}
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
            </Button>

            {role === 'subscriber' && (
              <p className="text-center text-sm text-[#5A5A5A]">
                Staff onboarding?{' '}
                <Link to="/register/supervisor" className="font-medium text-[#9F1D35] hover:text-[#7A1628]">
                  Use the internal form
                </Link>
              </p>
            )}
            {role === 'supervisor' && (
              <p className="text-center text-sm text-[#5A5A5A]">
                Registering as a subscriber?{' '}
                <Link to="/register" className="font-medium text-[#9F1D35] hover:text-[#7A1628]">
                  Use subscriber registration
                </Link>
              </p>
            )}
          </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
